import { Monitor, Moon, Sun } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  useTheme,
} from "@nexus/ui"
import { useSidebarStore } from "@/hooks/useSidebarStore"
import { useDisplayStore, type Density } from "@/hooks/useDisplayStore"

interface SettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const { collapsed, setCollapsed } = useSidebarStore()
  const { density, setDensity } = useDisplayStore()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("sidebar.settings")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* ── Appearance ─────────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[var(--muted-foreground)]">
              {t("settings.appearance")}
            </h3>
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-[var(--muted-foreground)]">
                {t("settings.theme")}
              </Label>
              <ToggleGroup
                type="single"
                value={theme}
                onValueChange={(v) => v && setTheme(v as "light" | "dark" | "system")}
                className="w-full"
              >
                <ToggleGroupItem value="light" className="flex-1 gap-1.5 text-xs">
                  <Sun className="h-3.5 w-3.5 shrink-0" />
                  {t("theme.light")}
                </ToggleGroupItem>
                <ToggleGroupItem value="dark" className="flex-1 gap-1.5 text-xs">
                  <Moon className="h-3.5 w-3.5 shrink-0" />
                  {t("theme.dark")}
                </ToggleGroupItem>
                <ToggleGroupItem value="system" className="flex-1 gap-1.5 text-xs">
                  <Monitor className="h-3.5 w-3.5 shrink-0" />
                  {t("theme.system")}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </section>

          {/* ── Sidebar ────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[var(--muted-foreground)]">
              {t("settings.sidebarBehavior")}
            </h3>
            <div className="flex items-center justify-between gap-4">
              <Label
                htmlFor="settings-sidebar-collapsed"
                className="cursor-pointer font-normal"
              >
                {t("settings.collapseByDefault")}
              </Label>
              {/* Intentional: toggling immediately applies + persists — live preview of the setting */}
              <Switch
                id="settings-sidebar-collapsed"
                checked={collapsed}
                onCheckedChange={setCollapsed}
              />
            </div>
          </section>

          {/* ── Display ────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[var(--muted-foreground)]">
              {t("settings.display")}
            </h3>
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-[var(--muted-foreground)]">
                {t("settings.tableDensity")}
              </Label>
              {/* TODO: wire density to data table row height once tables adopt useDisplayStore */}
              <ToggleGroup
                type="single"
                value={density}
                onValueChange={(v) => v && setDensity(v as Density)}
                className="w-full"
              >
                <ToggleGroupItem value="compact" className="flex-1 text-xs">
                  {t("settings.compact")}
                </ToggleGroupItem>
                <ToggleGroupItem value="comfortable" className="flex-1 text-xs">
                  {t("settings.comfortable")}
                </ToggleGroupItem>
                <ToggleGroupItem value="spacious" className="flex-1 text-xs">
                  {t("settings.spacious")}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
